import { CloseCircleOutlined, SoundOutlined } from "@ant-design/icons";
import { AnyNode } from "@p0security/shared/types/assessment/data/index";
import { Button, Form, Input, Modal } from "@p0security/ui";
import { compact } from "lodash";
import pluralize from "pluralize";
import { useCallback, useEffect, useState } from "react";

import { MonitorActionProps } from "../pages/MonitorResults";
import { useFindingsStateUpdates } from "./finding/useFindingsStateUpdates";

/** Bulk ignore / un-ignore of findings */
export const FindingsIgnoreModal: React.FC<MonitorActionProps> = ({
  allNodes,
  findingFor,
  selectedNodes,
  state,
}) => {
  const [toIgnore, setToIgnore] = useState<AnyNode[]>([]);
  const [notesForm] = Form.useForm<{ notes: string }>();

  const onIgnore = useCallback(
    () => setToIgnore(selectedNodes?.length ? selectedNodes : (allNodes ?? [])),
    [allNodes, selectedNodes]
  );
  const onCancelIgnore = useCallback(() => setToIgnore([]), []);
  const isIgnoring = state === "open";
  const selectedCount = selectedNodes?.length ?? 0;

  const { ignoreFindings, unignoreFindings } = useFindingsStateUpdates();

  const submitUnignore = useCallback(async () => {
    const findings = compact(toIgnore.map(findingFor));
    await unignoreFindings(findings);
    setToIgnore([]);
  }, [findingFor, unignoreFindings, toIgnore]);

  // Avoid persisting form state between modal openings
  useEffect(() => {
    if (toIgnore.length > 0) {
      notesForm.setFieldValue("notes", undefined);
    }
  }, [notesForm, toIgnore]);

  const submitIgnore = useCallback(
    async ({ notes }: { notes: string }) => {
      const findings = compact(toIgnore.map(findingFor));
      await ignoreFindings(findings, notes);
      setToIgnore([]);
    },
    [findingFor, ignoreFindings, toIgnore]
  );

  const confirmSection = (
    <>
      <Form.Item>
        {state === "open" ? "Ignore" : "Un-ignore"} {toIgnore.length}{" "}
        {pluralize("finding", toIgnore.length)}?
      </Form.Item>
      <Form.Item>
        <Button htmlType="submit" type="primary">
          OK
        </Button>
      </Form.Item>
    </>
  );

  return (
    <>
      <Button
        icon={isIgnoring ? <CloseCircleOutlined /> : <SoundOutlined />}
        onClick={onIgnore}
        disabled={!allNodes?.length}
      >
        {isIgnoring
          ? selectedCount > 0
            ? `Ignore ${selectedCount} ${pluralize("finding", selectedCount)}`
            : "Ignore"
          : "Un-ignore"}
      </Button>
      <Modal
        footer={false}
        maskClosable={false}
        open={toIgnore.length > 0}
        onCancel={onCancelIgnore}
        /* eslint-disable-line react/forbid-component-props */ style={
          state === "open" ? { minWidth: "600px" } : {}
        }
      >
        {state === "open" ? (
          <Form
            form={notesForm}
            onFinish={submitIgnore}
            preserve={false}
            // Lower hard-coded width so close doesn't float over input
            // Note that antd overflow behavior means we can't just use a margin :(
            /* eslint-disable-line react/forbid-component-props */ style={{
              width: "530px",
            }}
          >
            <Form.Item label="Notes" name="notes">
              <Input.TextArea
                placeholder="Enter any notes or justification here..."
                autoSize={{ minRows: 4 }}
              />
            </Form.Item>
            {confirmSection}
          </Form>
        ) : state === "ignored" ? (
          <Form onFinish={submitUnignore}>{confirmSection}</Form>
        ) : (
          (() => {
            throw new Error("Unexpected finding state in ignore modal");
          })()
        )}
      </Modal>
    </>
  );
};
